/**
 * useInventory — what the signed-in person owns from the coin shop.
 *
 * Every shop purchase lands in `user_inventory`. Until this hook existed
 * nothing read those rows, so purchases had no deliverable. Items are
 * "owned" while `consumed_at` is null and any `expires_at` is in the future.
 */
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface InventoryItem {
  id: string;
  sku: string;
  granted_at: string;
  expires_at: string | null;
  consumed_at: string | null;
  metadata: Record<string, unknown>;
  /** Title/description/kind joined from the shop catalog. */
  title: string;
  description: string | null;
  kind: string;
  price: number;
}

export interface ActiveCosmetics {
  frame?: string;
  name?: string;
  [slot: string]: string | undefined;
}

function isActive(row: { consumed_at: string | null; expires_at: string | null }): boolean {
  if (row.consumed_at) return false;
  if (row.expires_at && new Date(row.expires_at).getTime() <= Date.now()) return false;
  return true;
}

export function useInventory() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['user-inventory', user?.id],
    queryFn: async (): Promise<InventoryItem[]> => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('user_inventory')
        .select(
          'id, sku, granted_at, expires_at, consumed_at, metadata, coin_shop_items(title, description, kind, price)',
        )
        .eq('user_id', user.id)
        .order('granted_at', { ascending: false });
      if (error) throw error;
      return (data ?? []).map((row: any) => ({
        id: row.id,
        sku: row.sku,
        granted_at: row.granted_at,
        expires_at: row.expires_at,
        consumed_at: row.consumed_at,
        metadata: (row.metadata ?? {}) as Record<string, unknown>,
        title: row.coin_shop_items?.title ?? row.sku,
        description: row.coin_shop_items?.description ?? null,
        kind: row.coin_shop_items?.kind ?? 'consumable',
        price: row.coin_shop_items?.price ?? 0,
      }));
    },
    enabled: !!user?.id,
    staleTime: 15_000,
  });

  const cosmetics = useQuery({
    queryKey: ['active-cosmetics', user?.id],
    queryFn: async (): Promise<ActiveCosmetics> => {
      if (!user?.id) return {};
      const { data } = await supabase
        .from('profiles')
        .select('active_cosmetics')
        .eq('user_id', user.id)
        .maybeSingle();
      return (data?.active_cosmetics ?? {}) as ActiveCosmetics;
    },
    enabled: !!user?.id,
    staleTime: 60_000,
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['user-inventory', user?.id] });
    queryClient.invalidateQueries({ queryKey: ['active-cosmetics', user?.id] });
    queryClient.invalidateQueries({ queryKey: ['coin-wallet', user?.id] });
  }, [queryClient, user?.id]);

  const items = query.data ?? [];
  const owned = items.filter(isActive);
  const spent = items.filter((i) => !isActive(i));

  const countOf = useCallback(
    (sku: string) => owned.filter((i) => i.sku === sku).length,
    [owned],
  );

  const activeUntil = useCallback(
    (sku: string): Date | null => {
      const rows = owned.filter((i) => i.sku === sku && i.expires_at);
      if (!rows.length) return null;
      return rows
        .map((r) => new Date(r.expires_at as string))
        .sort((a, b) => b.getTime() - a.getTime())[0];
    },
    [owned],
  );

  /** Marks one owned copy as used. Returns the inventory id so a failed
   *  delivery can be put back with `restore`. */
  const consume = useMutation({
    mutationFn: async ({ sku, ref }: { sku: string; ref?: string }) => {
      const { data, error } = await supabase.rpc('consume_inventory_item', {
        _sku: sku,
        _ref: ref ?? null,
      });
      if (error) throw error;
      return data as { ok: boolean; reason?: string; inventory_id?: string };
    },
    onSuccess: invalidate,
  });

  const restore = useMutation({
    mutationFn: async (inventoryId: string) => {
      const { error } = await supabase.rpc('restore_inventory_item', { _inventory_id: inventoryId });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const refund = useMutation({
    mutationFn: async ({ inventoryId, reason }: { inventoryId: string; reason?: string }) => {
      const { data, error } = await supabase.rpc('refund_inventory_purchase', {
        _inventory_id: inventoryId,
        _reason: reason ?? 'undeliverable',
      });
      if (error) throw error;
      return data as { ok: boolean; amount?: number };
    },
    onSuccess: invalidate,
  });

  const equip = useMutation({
    mutationFn: async ({ sku, equip: on }: { sku: string; equip: boolean }) => {
      const { data, error } = await supabase.rpc('equip_cosmetic', { _sku: sku, _equip: on });
      if (error) throw error;
      return data as { ok: boolean; reason?: string };
    },
    onSuccess: invalidate,
  });

  return {
    items,
    owned,
    spent,
    isLoading: query.isLoading,
    refetch: query.refetch,
    cosmetics: cosmetics.data ?? {},
    countOf,
    activeUntil,
    ownsActive: (sku: string) => countOf(sku) > 0,
    consume,
    restore,
    refund,
    equip,
  };
}

/** Lightweight read for surfaces that only need to know "do I own this?". */
export function useOwnsItem(sku: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['owns-item', user?.id, sku],
    queryFn: async (): Promise<number> => {
      if (!user?.id) return 0;
      const { count } = await supabase
        .from('user_inventory')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('sku', sku)
        .is('consumed_at', null);
      return count ?? 0;
    },
    enabled: !!user?.id,
    staleTime: 30_000,
  });
}
